/**
 * localStorage adapter behind `sourceControlToggleAtom`.
 *
 * `atomWithStorage`'s default adapter passes two localStorage realities
 * straight through to the app:
 *
 *   - `setItem` throws `QuotaExceededError` once the ORIGIN is full, and the
 *     origin is shared. Production RUM shows the same users failing on this
 *     key, on `FLAGSMITH_DB_*` and on a long tail of
 *     `heidi-chat-draft-<chatId>` keys inside one session, so the write that
 *     throws is rarely the write that filled the disk. The backend owns the
 *     canonical selections (`User.evidence_settings.source_control`), which
 *     makes this a cache: a refused write has to degrade to "not cached"
 *     instead of taking the surrounding flow down with it.
 *   - The persisted shape has grown field by field since before the BE sync
 *     existed, so blobs written by older builds are still on disk without
 *     `integrationPartners` — which `getEnabledChatSourceBuckets` reads via
 *     `Object.values(...)`, throwing on `undefined`. Reads are repaired field
 *     by field rather than trusted.
 */
import { getSafeLocalStorage } from '@/lib/jotai-storage';
import { logger } from '@/utils/logger/logger';
import { SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY } from '@heidi/evidence/contracts';
import { createJSONStorage } from 'jotai/utils';

import type { SourceControlToggleState } from './sourceControlAtoms';

/**
 * A fully populated blob is a few KB. Anything past this is corruption or an
 * accumulation bug, and caching it would make this key part of the problem it
 * is currently only a victim of.
 *
 * Enforced on reads as well as writes. Every blob already on disk predates the
 * bound, so a read that trusted its size would hand `JSON.parse` and the
 * sanitizer an unbounded string on every mount, and would keep doing so until
 * the user happened to toggle something. Dropping it costs nothing the backend
 * cannot restore.
 */
const MAX_CACHED_CHARS = 32_768;

const warnedFailures = new Set<string>();

/**
 * Deduped per failure kind, not per key: a read that fails and a write that
 * later fails are different problems, and collapsing them onto the key would
 * let the first one silence the rest for the life of the tab.
 */
const warnOnce = (dedupeToken: string, message: string, error?: unknown): void => {
  if (warnedFailures.has(dedupeToken)) return;
  warnedFailures.add(dedupeToken);
  logger.warn(message, error);
};

const buildStringStorage = () => {
  const safe = getSafeLocalStorage((operation, key, error) => {
    warnOnce(
      `${operation}:${key}`,
      `[sourceControl] localStorage ${operation} failed for ${key}`,
      error
    );
  });

  return {
    ...safe,
    getItem: (key: string) => {
      const value = safe.getItem(key);
      if (value !== null && value.length > MAX_CACHED_CHARS) {
        safe.removeItem(key);
        warnOnce(
          `oversized-get:${key}`,
          `[sourceControl] discarded an oversized cached value for ${key}`
        );
        return null;
      }
      return value;
    },
    setItem: (key: string, value: string) => {
      if (value.length > MAX_CACHED_CHARS) {
        safe.removeItem(key);
        warnOnce(
          `oversized-set:${key}`,
          `[sourceControl] refused to cache an oversized value for ${key}`
        );
        return;
      }
      safe.setItem(key, value);
    },
    // jotai wires its own cross-tab `storage` listener only when the adapter IS
    // `window.localStorage`, which a safe wrapper never is. Supplying one keeps
    // the cross-tab propagation this atom had under the default adapter.
    ...(typeof window === 'undefined'
      ? {}
      : {
          subscribe: (key: string, callback: (value: string | null) => void) => {
            const onStorage = (event: StorageEvent) => {
              if (event.storageArea === window.localStorage && event.key === key) {
                callback(event.newValue);
              }
            };
            window.addEventListener('storage', onStorage);
            return () => window.removeEventListener('storage', onStorage);
          },
        }),
  };
};

let stringStorage: ReturnType<typeof buildStringStorage> | undefined;

const getStringStorage = () => (stringStorage ??= buildStringStorage());

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const asMode = (
  value: unknown,
  fallback: 'all' | 'collections'
): 'all' | 'collections' =>
  value === 'all' || value === 'collections' ? value : fallback;

const asIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? Array.from(new Set(value.filter((id): id is string => typeof id === 'string')))
    : [];

/**
 * Every reader tests `integrationPartners[id] === true`, so a `false` entry is
 * indistinguishable from an absent one — and nothing ever deletes one, which
 * makes it the only monotonically growing field in the shape.
 */
const asEnabledPartners = (value: unknown): Record<string, boolean> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  const enabled: Record<string, boolean> = {};
  for (const [partnerId, isEnabled] of Object.entries(value)) {
    if (isEnabled === true) enabled[partnerId] = true;
  }
  return enabled;
};

export function sanitizeSourceControlToggleState(
  value: unknown,
  defaults: SourceControlToggleState
): SourceControlToggleState {
  // Missing key or unparseable JSON: jotai hands the initial value straight
  // back, and re-deriving it here would cost every mount a fresh identity.
  if (value === defaults) return defaults;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return defaults;
  }

  const raw = value as Record<string, unknown>;
  return {
    localGuidelines: asBoolean(raw.localGuidelines, defaults.localGuidelines),
    primaryEvidence: asBoolean(raw.primaryEvidence, defaults.primaryEvidence),
    myLibrary: asBoolean(raw.myLibrary, defaults.myLibrary),
    libraryMode: asMode(raw.libraryMode, defaults.libraryMode),
    selectedCollectionIds: asIds(raw.selectedCollectionIds),
    webCollections: asBoolean(raw.webCollections, defaults.webCollections),
    webCollectionsMode: asMode(raw.webCollectionsMode, defaults.webCollectionsMode),
    selectedWebCollectionIds: asIds(raw.selectedWebCollectionIds),
    integrationPartners: asEnabledPartners(raw.integrationPartners),
    webSearch: asBoolean(raw.webSearch, defaults.webSearch),
    includeUncollected: asBoolean(raw.includeUncollected, defaults.includeUncollected),
  };
}

/**
 * Storage for `sourceControlToggleAtom`: quota-safe writes, and both reads and
 * writes narrowed to the current shape so a legacy blob cannot reach the app
 * and a dead partner entry cannot reach the disk.
 */
export function createSourceControlToggleStorage(defaults: SourceControlToggleState) {
  const json = createJSONStorage<SourceControlToggleState>(getStringStorage);
  const subscribe = json.subscribe;

  return {
    ...json,
    getItem: (key: string, initialValue: SourceControlToggleState) =>
      sanitizeSourceControlToggleState(json.getItem(key, initialValue), defaults),
    setItem: (key: string, newValue: SourceControlToggleState) =>
      json.setItem(key, sanitizeSourceControlToggleState(newValue, defaults)),
    ...(subscribe
      ? {
          subscribe: (
            key: string,
            callback: (value: SourceControlToggleState) => void,
            initialValue: SourceControlToggleState
          ) =>
            subscribe(
              key,
              (value) => callback(sanitizeSourceControlToggleState(value, defaults)),
              initialValue
            ),
        }
      : {}),
  };
}

/** Last backend default applied for a user with nothing saved (see the key's doc). */
export function readAppliedServerDefault(
  defaults: SourceControlToggleState
): SourceControlToggleState | null {
  const raw = getStringStorage().getItem(
    SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY
  );
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  // A non-object row is no default at all; sanitizing it would invent the stock one.
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  return sanitizeSourceControlToggleState(parsed, defaults);
}

/** Written sanitized, so it compares equal to the sanitized toggle cache. */
export function writeAppliedServerDefault(
  value: SourceControlToggleState | null,
  defaults: SourceControlToggleState
): void {
  if (value === null) {
    getStringStorage().removeItem(SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY);
    return;
  }
  getStringStorage().setItem(
    SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY,
    JSON.stringify(sanitizeSourceControlToggleState(value, defaults))
  );
}
